import React, { useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '../../../../../../shared/components';
import {
  BuildingConstruction,
  LandUseChange,
  RawMaterialUse,
  RoadPavement,
  ConstructionEquipment,
  EnergyConsumption,
  RoadMaintenance,
} from '../../../../../../services/types/Calculator';
import LandUseChangeSection from '../Modules/LandUseChangeSection';
import RawMaterialUseSection from '../Modules/RawMaterialUseSection';
import RoadPavementSection from '../Modules/RoadPavementSection';
import BuildingConstructionSection from '../Modules/BuildingConstructionSection';
import ConstructionEquipmentSection from '../Modules/ConstructionEquipmentSection';
import EnergyConsumptionSection from '../Modules/EnergyConsumptionSection';
import NumberFormatterSinSeparador from '../../NumberFormatterSinSeparador';
import { tooltipPreventOverflowModifiers } from '../../../../../../shared/misc/utils';

export interface TeamMember {
  imgPath?: string;
  name: string;
  initials: string;
}
interface Props {
  landUseChange: LandUseChange;
  rawMaterialUse: RawMaterialUse;
  roadPavement: RoadPavement;
  buildingConstruction: BuildingConstruction;
  energyConsumptionConstruction: EnergyConsumption;
  constructionEquipment: ConstructionEquipment;
  setLandUseChange: React.Dispatch<React.SetStateAction<LandUseChange>>;
  setRawMaterialUse: React.Dispatch<React.SetStateAction<RawMaterialUse>>;
  setRoadPavement: React.Dispatch<React.SetStateAction<RoadPavement>>;
  setEnergyConsumptionConstruction: React.Dispatch<React.SetStateAction<EnergyConsumption>>;
  setBuildingConstruction: React.Dispatch<React.SetStateAction<BuildingConstruction>>;
  setConstructionEquipment: React.Dispatch<React.SetStateAction<ConstructionEquipment>>;
  roadMaintenance: RoadMaintenance;
  setRoadMaintenance: React.Dispatch<React.SetStateAction<RoadMaintenance>>;
}

const ConstructionTab: React.FC<Props> = ({
  landUseChange,
  rawMaterialUse,
  roadPavement,
  buildingConstruction,
  energyConsumptionConstruction,
  constructionEquipment,
  setLandUseChange,
  setRawMaterialUse,
  setRoadPavement,
  setEnergyConsumptionConstruction,
  setBuildingConstruction,
  setConstructionEquipment,
  roadMaintenance,
  setRoadMaintenance,
}) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const icon = `${process.env.PUBLIC_URL}/img/GPS.svg`;

  const handleRowToggle = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((rowIndex) => rowIndex !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const chevron = (index: number) =>
    expandedRows.includes(index) ? 'chevron-down' : 'chevron-right';

  return (
    <div className="timeline-list">
      {landUseChange && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(0)}
                text={landUseChange.name}
                type="button"
                onClick={() => handleRowToggle(0)}
              />
              {expandedRows.includes(0) && (
                <>
                  <span id={`tr-landUse-${landUseChange.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-landUse-${landUseChange.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.LandUseChange')}
                  </UncontrolledTooltip>

                  <span id={`tr-landUselink-${landUseChange.moduleId}`}>
                    <a
                      title="LandUseChangeInfo"
                      aria-label={t('moduleInstructions.LandUseChange')}
                      href={landUseChange.infoUrl ? landUseChange.infoUrl : ''}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={icon} alt="" className="calculator-icon-svg" />
                    </a>
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-landUselink-${landUseChange.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    Click Here to view IPCC Map to determine Land Cover Type if Unknown
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(0) && landUseChange.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={landUseChange.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          <span className="deleted-toolkit" />
          {expandedRows.includes(0) && (
            <LandUseChangeSection
              landUseChange={landUseChange}
              onUpdateLandUseChange={(updatedLandUseChange) =>
                setLandUseChange(updatedLandUseChange)
              }
            />
          )}
        </div>
      )}

      {buildingConstruction && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(1)}
                text={buildingConstruction.name}
                type="button"
                onClick={() => handleRowToggle(1)}
              />
              {expandedRows.includes(1) && (
                <>
                  <span id={`tr-buildingConstruction-${buildingConstruction.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-buildingConstruction-${buildingConstruction.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.BuildingConstruction')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(1) && buildingConstruction.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={buildingConstruction.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(1) && (
            <BuildingConstructionSection
              buildingConstruction={buildingConstruction}
              onUpdateBuildingConstruction={(updatedBuildingConstruction) =>
                setBuildingConstruction(updatedBuildingConstruction)
              }
            />
          )}
        </div>
      )}

      {roadPavement && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(2)}
                text={roadPavement.name}
                type="button"
                onClick={() => handleRowToggle(2)}
              />
              {expandedRows.includes(2) && (
                <>
                  <span id={`tr-roadPavement-${roadPavement.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-roadPavement-${roadPavement.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.RoadPavement')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(2) && roadPavement.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={roadPavement.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(2) && (
            <RoadPavementSection
              roadPavement={roadPavement}
              onUpdateRoadPavement={(updatedRoadPavement) => setRoadPavement(updatedRoadPavement)}
              roadMaintenance={roadMaintenance}
              onUpdateRoadMaintenance={(updatedRoadMaintenance) =>
                setRoadMaintenance(updatedRoadMaintenance)
              }
            />
          )}
        </div>
      )}

      {energyConsumptionConstruction && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(3)}
                text={energyConsumptionConstruction.name}
                type="button"
                onClick={() => handleRowToggle(3)}
              />
              {expandedRows.includes(3) && (
                <>
                  <span
                    id={`tr-energyConsumptionConstruction-${energyConsumptionConstruction.moduleId}`}
                  >
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-energyConsumptionConstruction-${energyConsumptionConstruction.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.EnergyConsumption')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(3) && energyConsumptionConstruction.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal:{' '}
                <NumberFormatterSinSeparador value={energyConsumptionConstruction.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(3) && (
            <EnergyConsumptionSection
              energyConsumption={energyConsumptionConstruction}
              onUpdateEnergyConsumption={(updatedEnergyConsumptionConstruction) =>
                setEnergyConsumptionConstruction(updatedEnergyConsumptionConstruction)
              }
            />
          )}
        </div>
      )}

      {constructionEquipment && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(4)}
                text={constructionEquipment.name}
                type="button"
                onClick={() => handleRowToggle(4)}
              />
              {expandedRows.includes(4) && (
                <>
                  <span id={`tr-constructionEquipement-${constructionEquipment.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-constructionEquipement-${constructionEquipment.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.ConstructionEquipmentA')}
                    <br />
                    {t('moduleInstructions.ConstructionEquipmentB')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(4) && constructionEquipment.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={constructionEquipment.subTotal} /> t
                CO<sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(4) && (
            <ConstructionEquipmentSection
              constructionEquipment={constructionEquipment}
              onUpdateConstructionEquipment={(updatedConstructionEquipment) =>
                setConstructionEquipment(updatedConstructionEquipment)
              }
            />
          )}
        </div>
      )}

      {rawMaterialUse && (
        <div className="timeline-list-row calculator active">
          <div className="timeline-row-header-calculator">
            <div className="timeline-row-title-calculator">
              <Button
                className="btn-tertiary py-0 px-1 calc-no-transition"
                icon={chevron(5)}
                text={rawMaterialUse.name}
                type="button"
                onClick={() => handleRowToggle(5)}
              />
              {expandedRows.includes(5) && (
                <>
                  <span id={`tr-rawMaterials-${rawMaterialUse.moduleId}`}>
                    <FontAwesomeIcon icon="info-circle" className="calculator-icon" />
                  </span>
                  <UncontrolledTooltip
                    className="calculator-instruction"
                    placement="bottom"
                    target={`tr-rawMaterials-${rawMaterialUse.moduleId}`}
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('moduleInstructions.RawMaterials')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            {!expandedRows.includes(5) && rawMaterialUse.subTotal != null && (
              <div className="timeline-row-subtotal-calculator">
                Subtotal: <NumberFormatterSinSeparador value={rawMaterialUse.subTotal} /> t CO
                <sub>2</sub>
              </div>
            )}
            <div className="timeline-row-empty-calculator" />
          </div>
          {expandedRows.includes(5) && (
            <RawMaterialUseSection
              rawMaterialUse={rawMaterialUse}
              onUpdateRawMaterialUse={(updatedRawMaterialUse) =>
                setRawMaterialUse(updatedRawMaterialUse)
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ConstructionTab;
